import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { SubscriptionPlan } from '../../models/platform-access.models';
import { User } from '../../services/auth.service';
import { BillingApiError, BillingSummary, BillingService } from '../../services/billing.service';

@Component({
  selector: 'app-billing-plans-page',
  standalone: true,
  imports: [DatePipe, RouterLink, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './billing-plans-page.component.html',
  styleUrls: ['./billing-plans-page.component.scss']
})
export class BillingPlansPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  isInitialLoading = true;
  hasInitialLoadError = false;
  isPortalBusy = false;
  pageMessage = typeof history !== 'undefined' ? history.state?.billingMessage ?? '' : '';
  pageError = '';
  currentUser: User | null = null;
  billing: BillingSummary | null = null;
  plans: SubscriptionPlan[] = [];

  constructor(
    private readonly billingService: BillingService
  ) {}

  ngOnInit(): void {
    this.loadPlans();
  }

  get hasPaidSubscription(): boolean {
    return (this.currentUser?.plan.priceEur ?? 0) > 0;
  }

  loadPlans(): void {
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;
    this.pageError = '';

    this.billingService.getPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.currentUser = state.currentUser;
            this.billing = state.billing;
            this.plans = state.plans;
            this.isInitialLoading = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.hasInitialLoadError = true;
            this.isInitialLoading = false;
          });
        }
      });
  }

  isCurrentPlan(plan: SubscriptionPlan): boolean {
    return this.currentUser?.plan.code === plan.code;
  }

  formatPlanPrice(plan: SubscriptionPlan | User['plan']): string {
    if (plan.priceEur === 0) {
      return 'Free';
    }

    return `${plan.priceEur} EUR / ${plan.billingInterval}`;
  }

  planFeatureList(plan: SubscriptionPlan): string[] {
    const features = [
      plan.entitlements.maxActiveWebsites === null
        ? 'Unlimited active websites'
        : `${plan.entitlements.maxActiveWebsites} active website${plan.entitlements.maxActiveWebsites === 1 ? '' : 's'}`,
      plan.entitlements.maxTeamMembers === null
        ? 'Unlimited team members'
        : `${plan.entitlements.maxTeamMembers} team member${plan.entitlements.maxTeamMembers === 1 ? '' : 's'}`,
      plan.entitlements.allowCustomDomain ? 'Custom domains included' : 'No custom domains',
      plan.entitlements.allowExport ? 'Export access included' : 'No export access',
      plan.entitlements.allowPublish ? 'Publishing included' : 'Upgrade required for publishing',
      `${this.capitalize(plan.entitlements.templateAccessLevel)} template access`,
      `${this.capitalize(plan.entitlements.supportLevel)} support`
    ];

    if (plan.paddleBillingEnabled) {
      features.unshift('Managed by Paddle secure billing');
    }

    return features;
  }

  actionLabel(plan: SubscriptionPlan): string {
    if (!this.currentUser || this.isCurrentPlan(plan)) {
      return 'Current plan';
    }

    if (plan.priceEur === 0) {
      return 'Move to Free';
    }

    if (this.currentUser.plan.priceEur === 0) {
      return 'Choose plan';
    }

    return plan.priceEur > this.currentUser.plan.priceEur ? 'Upgrade now' : 'Schedule downgrade';
  }

  actionClass(plan: SubscriptionPlan): string {
    if (this.isCurrentPlan(plan)) {
      return 'btn btn-outline-secondary';
    }

    if (!this.currentUser) {
      return 'btn btn-primary';
    }

    if (plan.priceEur === 0) {
      return 'btn btn-outline-danger';
    }

    return plan.priceEur > this.currentUser.plan.priceEur ? 'btn btn-primary' : 'btn btn-outline-secondary';
  }

  scheduledChangeLabel(): string | null {
    const action = this.billing?.scheduledChangeAction;
    if (!action) {
      return null;
    }

    if (action === 'cancel_to_free') {
      return 'Free';
    }

    if (action.startsWith('switch_to:')) {
      const planCode = action.slice('switch_to:'.length);
      const plan = this.plans.find((entry) => entry.code === planCode);
      return plan?.name ?? planCode;
    }

    return action;
  }

  openBillingPortal(): void {
    this.pageError = '';
    this.isPortalBusy = true;

    this.billingService.createPortalSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => {
          this.runInAngular(() => {
            this.isPortalBusy = false;
          });
          const portalWindow = window.open(url, '_blank', 'noopener,noreferrer');
          if (!portalWindow) {
            this.runInAngular(() => {
              this.pageError = 'The billing portal was blocked by the browser. Allow popups and try again.';
            });
          }
        },
        error: (error: BillingApiError) => {
          this.runInAngular(() => {
            this.pageError = error.message;
            this.isPortalBusy = false;
          });
        }
      });
  }

  private capitalize(value: string): string {
    if (!value) {
      return '';
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
